import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import {
  Controller,
  type ControllerRenderProps,
  type DefaultValues,
  type FieldValues,
  FormProvider,
  type Path,
  type Resolver,
  type SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form';
import type { ZodType } from 'zod';
import { Btn, type BtnVariant } from '@/design-system/primitives/Btn/Btn';
import styles from './Form.module.css';

interface FormProps<T extends FieldValues> {
  defaultValues?: DefaultValues<T>;
  onSubmit: SubmitHandler<T>;
  schema?: ZodType<T>;
  children: ReactNode;
  className?: string;
  id?: string;
}

export function Form<T extends FieldValues>({
  defaultValues,
  onSubmit,
  schema,
  children,
  className,
  id,
}: FormProps<T>) {
  // zod 4 + RHF: zodResolver's typing is stricter than the schema generic we
  // accept here. The runtime is correct; cast at the boundary.
  const resolver = schema
    ? // biome-ignore lint/suspicious/noExplicitAny: zod 4 / RHF generic mismatch
      (zodResolver(schema as any) as unknown as Resolver<T>)
    : undefined;
  const methods = useForm<T>({
    defaultValues,
    mode: 'onTouched',
    resolver,
  });
  return (
    <FormProvider {...methods}>
      <form
        id={id}
        onSubmit={methods.handleSubmit(onSubmit)}
        className={`${styles.form} ${className ?? ''}`}
        noValidate
      >
        {children}
      </form>
    </FormProvider>
  );
}

type FormFieldRenderProps<T extends FieldValues> = ControllerRenderProps<T, Path<T>> & {
  id: string;
};

interface FormFieldProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  hint?: string;
  required?: boolean;
  children: (field: FormFieldRenderProps<T>) => ReactNode;
}

export function FormField<T extends FieldValues>({
  name,
  label,
  hint,
  required,
  children,
}: FormFieldProps<T>) {
  const { control, formState } = useFormContext<T>();
  const error = formState.errors[name];
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined;
  const fieldId = `field-${String(name)}`;
  return (
    <div className={styles.field}>
      <label htmlFor={fieldId} className={styles.label}>
        {label}
        {required && <span className={styles.required}> *</span>}
      </label>
      {hint && <div className={styles.hint}>{hint}</div>}
      <Controller
        control={control}
        name={name}
        rules={required ? { required: 'Verplicht veld' } : undefined}
        render={({ field }) => <>{children({ ...field, id: fieldId, ref: field.ref })}</>}
      />
      {errorMessage && (
        <div role="alert" className={styles.error}>
          {errorMessage}
        </div>
      )}
    </div>
  );
}

interface FormSubmitButtonProps {
  children: ReactNode;
  variant?: BtnVariant;
  className?: string;
}

export function FormSubmitButton({
  children,
  variant = 'primary',
  className,
}: FormSubmitButtonProps) {
  const { formState } = useFormContext();
  return (
    <Btn type="submit" variant={variant} disabled={formState.isSubmitting} className={className}>
      {formState.isSubmitting ? 'Bezig…' : children}
    </Btn>
  );
}
